<template>
    <div class="position-relative">
        <div class="row" v-if="patient_">
            <!--LEFT COLUMN-->
            <div class="col-12 col-md-6">
                <!--PROFILE-->
                <patient-profile
                    :patient="patient_"
                    @patient-saved="fetchPatient()"
                ></patient-profile>
                <!--RESPONSIBLE PARTY-->
                <emergency-info
                    :person="patient_"
                    :endpoint="`/api/patients/`"
                    :contacts-property="`responsible_party_contacts`"
                    @person-saved="fetchPatient()"
                ></emergency-info>
                <!--EMERGENCY INFO-->
                <emergency-info
                    :person="patient_"
                    :endpoint="`/api/patients/`"
                    :contacts-property="`emergency_contacts`"
                    @person-saved="fetchPatient()"
                ></emergency-info>
                <!--THERAPY INFO-->
                <patient-therapists
                    :user="user"
                    :organization="organization"
                    :patient="patient_"
                    @update-resource="fetchPatient()"
                ></patient-therapists>
                <!--TRAITS-->
                <patient-traits
                    :patient="patient_"
                    :special-conditions="specialConditions"
                    @patient-saved="fetchPatient()"
                ></patient-traits>
                <!--SCHOOL SKILLS-->
                <patient-skills
                    :patient="patient_"
                    @patient-saved="fetchPatient()"
                ></patient-skills>
            </div>
            <!--RIGHT COLUMN-->
            <div class="col-12 col-md-6">
                <!--SCHOOL  CONTACT INFO-->
                <emergency-info
                    :person="patient_"
                    :endpoint="`/api/patients/`"
                    :contacts-property="`school_schedule_change_contacts`"
                    @person-saved="fetchPatient()"
                ></emergency-info>

                <!-- PRIMARY INSURANCE INFORMATION -->
                <patient-insurance-info
                    v-if="
                        ['professional_platinum'].includes(organization.type) &&
                            canViewClientFunding
                    "
                    :patient="patient_"
                    :fundings="insuranceProviders"
                    @refresh-fundings="fetchFundings()"
                ></patient-insurance-info>

                <template v-if="$env === 'demo'">
                    <!--REFERRING DOCTOR-->
                    <div style="border: dotted 1px #bfbfbf;" class="p-2 widget my-2">
                        <div class="row">
                            <div class="col-12">
                                <a class="font-weight-bold link-primary float-right text-primary"
                                    >Edit</a
                                >
                                <p class="text-uppercase font-weight-bold">Referring Doctor</p>
                                <div class="d-flex justify-content-between">
                                    <p class="mb-0">Doctor name:</p>
                                    <p class="mb-0">Address:</p>
                                    <p class="mb-0">NPI #:</p>
                                </div>
                                <div class="d-flex justify-content-between">
                                    <p class="mb-0">Taxonomy:</p>
                                    <p class="mb-0">Organization:</p>
                                    <p></p>
                                </div>
                                <p class="mb-0">Phone:</p>
                                <p class="mb-0">Fax:</p>
                                <p class="mb-0">E-mail:</p>
                                <p>Prescription expire: 01/01/2019</p>
                                <a class="font-weight-bold text-uppercase link-primary text-primary"
                                    >Add doctor<i class="fa fa-angle-right ml-2"></i
                                ></a>
                            </div>
                        </div>
                    </div>
                    <!--CLINIC HISTORY-->
                    <div style="border: dotted 1px #bfbfbf;" class="p-2 widget my-2">
                        <div class="row">
                            <div class="col-12">
                                <a class="font-weight-bold link-primary float-right text-primary"
                                    >Edit</a
                                >
                                <p class="text-uppercase font-weight-bold mb-0">
                                    Clinic claim history
                                </p>
                                <p class="mb-0 link">324 Claims</p>
                                <p class="mb-0 link">301 Paid</p>
                                <p class="mb-0 link">20 Underpaid</p>
                                <p class="mb-0 link">2 Pending</p>
                                <p class="mb-0 link">1 Denied</p>

                                <p class="text-uppercase font-weight-bold mb-0 mt-2">
                                    Clinic stats
                                </p>
                                <p class="mb-0"># Cancellations: 30%</p>
                                <p class="mb-0"># months as client</p>
                            </div>
                        </div>
                    </div>
                </template>

                <!--CREDIT CARD-->
                <patient-credit-card
                    v-if="
                        ['professional_platinum'].includes(organization.type) &&
                            canViewClientCreditCard
                    "
                    :patient="patient_"
                    :organization="organization"
                    @update-patient="fetchPatient()"
                ></patient-credit-card>
            </div>
        </div>

        <patient-create-receipt
            :organization="organization"
            :id="patient.id"
        ></patient-create-receipt>

        <internal-notes
            v-if="canViewClientNotes"
            :api-endpoints="internalNotesEndpoint"
            :user-type="'client'"
        ></internal-notes>

        <patient-funding
            v-if="['professional_platinum'].includes(organization.type) && canViewClientFunding"
            :patient="patient_"
            :fundings="fundings"
            @refresh-fundings="fetchFundings()"
            :cpt-codes="organizationCptCodes"
            :service-codes="organizationServiceCodes"
            :diagnoses="organizationDiagnoses"
            :organization="organization"
        >
        </patient-funding>
    </div>
</template>

<script>
import PatientProfile from "./PatientProfile";
import EmergencyInfo from "../widgets/EmergencyInfoWidget";
import PatientTherapists from "./PatientTherapists";
import InternalNotes from "../ui/InternalNotes";
import PatientFunding from "./PatientFunding";
import PatientTraits from "./PatientTraits";
import PatientSkills from "./PatientSkills";
import PatientCreditCard from "./PatientCreditCard";
import PatientInsuranceInfo from "./PatientInsuranceInfo";
import PatientCreateReceipt from "./PatientCreateReceipt";
import { fixDatePickers } from "../../plugins/fixDatePickers";
import { permissions } from "../../plugins/permissions";

export default {
    name: "PatientOverview",
    props: [
        "user",
        "patient",
        "organization",
        "organizationServiceCodes",
        "organizationCptCodes",
        "organizationDiagnoses",
        "specialConditions",
    ],
    components: {
        PatientProfile,
        EmergencyInfo,
        PatientTherapists,
        InternalNotes,
        PatientFunding,
        PatientTraits,
        PatientSkills,
        PatientCreditCard,
        PatientInsuranceInfo,
        PatientCreateReceipt,
    },
    data() {
        return {
            patient_: {},
            fundings: [],
        };
    },
    mixins: [fixDatePickers, permissions],
    computed: {
        internalNotesEndpoint() {
            return {
                get: `/api/patients/${this.patient.id}/notes`,
                post: `/api/patients/${this.patient.id}/notes`,
            };
        },
        insuranceProviders() {
            return this.fundings.filter(
                f => _.get(f, "source.type", null) === "insurance_provider"
            );
        },
    },
    watch: {
        patient() {
            if (this.patient) {
                this.patient_ = { ...this.patient };
                this.fetchFundings();
            }
        },
    },
    methods: {
        fetchPatient() {
            axios
                .get(`/api/patients/${this.patient.id}`)
                .then(r => {
                    this.patient_ = r.data.data;
                })
                .catch(e => {
                    console.error(e);
                });
        },
        fetchFundings() {
            if (!this.canViewClientFunding) return;
            axios
                .get(`/api/patients/${this.patient.id}/fundings?with=archived`)
                .then(r => {
                    this.fundings = r.data.data;
                })
                .catch(error => {
                    console.error(error);
                });
        },
    },
};
</script>

<style scoped lang="scss">
.profile-image {
    min-width: 5rem;
    max-width: 5rem;
    min-height: 5rem;
    max-height: 5rem;
}

.source-details {
    column-count: 2;
}
</style>
